import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { updateUser } from '@/services/user';
import { userSchema } from '@/schema/user-schema';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { useTranslation } from 'react-i18next';
import { Pencil, ShieldCheck, User2 } from 'lucide-react';
import { toast } from 'sonner';
import { errorHandler } from '@/lib/utils';
import { useAuthenticationDispatch } from '@/hooks/use-authentication';
// Roles are hierarchical (ROLE_INTERNAL_ADMIN ⊃ ROLE_INTERNAL_EMPLOYEE ⊃ ROLE_USER),
// so showing them flat reads as "Internal admin · User · Internal employee" — noisy
// for a single user. Surface the strongest one instead.
const ROLE_RANK = {
    ROLE_SUPER_ADMIN: 50,
    ROLE_ADMIN: 40,
    ROLE_INTERNAL_ADMIN: 30,
    ROLE_INTERNAL_EMPLOYEE: 20,
    ROLE_USER: 10,
    ROLE_DEMO: 5,
};
function strongestRole(roles) {
    let best = null;
    let bestRank = -1;
    for (const role of roles) {
        const rank = ROLE_RANK[role] ?? 0;
        if (rank > bestRank) {
            bestRank = rank;
            best = role;
        }
    }
    return best;
}
export const IdentityCard = ({ user }) => {
    const { t } = useTranslation(['profile', 'common']);
    const dispatch = useAuthenticationDispatch();
    const [open, setOpen] = React.useState(false);
    const form = useForm({
        resolver: zodResolver(userSchema),
        defaultValues: {
            firstName: user.firstName,
            lastName: user.lastName,
        },
    });
    const { mutate, isPending } = useMutation({
        mutationFn: updateUser,
        onSuccess: (data) => {
            toast.success(data.message);
            setOpen(false);
            dispatch({ type: 'UPDATE_USER', user: data.user });
        },
        onError: errorHandler,
    });
    const onSubmit = React.useCallback((data) => {
        mutate({ data });
    }, [mutate]);
    return (<Card>
            <CardHeader>
                <CardTitle>{t('identity.title')}</CardTitle>
                <CardDescription>{t('identity.desc')}</CardDescription>
            </CardHeader>

            <CardContent className="flex items-center gap-6">
                <Avatar className="h-16 w-16">
                    <AvatarImage src={user.avatarUrl}/>
                    <AvatarFallback>
                        {user.firstName[0]}
                        {user.lastName[0]}
                    </AvatarFallback>
                </Avatar>

                <div className="flex-1 space-y-1">
                    <div className="font-medium">
                        {user.firstName} {user.lastName}
                    </div>

                    <div className="text-sm text-muted-foreground">{user.email}</div>

                    <div className="flex gap-2 pt-2">
                        {(() => {
            const role = strongestRole(user.roles);
            if (role === null)
                return null;
            const slug = role.replace('ROLE_', '').toLowerCase();
            return (<Badge variant="secondary" className="gap-1">
                                    <ShieldCheck className="h-3 w-3"/>
                                    {t(`identity.roles.${slug}`, { defaultValue: role.replace('ROLE_', '') })}
                                </Badge>);
        })()}

                        {!user.enabled && <Badge variant="destructive">{t('identity.disabled')}</Badge>}
                    </div>
                </div>

                <Dialog open={open} onOpenChange={(open) => {
            if (open) {
                form.reset({ firstName: user.firstName, lastName: user.lastName });
            }
            setOpen(open);
        }}>
                    <DialogTrigger asChild>
                        <Button variant="outline">
                            <Pencil />
                            {t('edit', { ns: 'common' })}
                        </Button>
                    </DialogTrigger>

                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>{t('identity.edit_dialog_title')}</DialogTitle>
                            <DialogDescription>{t('identity.edit_dialog_desc')}</DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-2">
                            <Form {...form}>
                                <form className="flex flex-col gap-4">
                                    <TextField control={form.control} name="firstName" label={t('first_name', { ns: 'common' })} placeholder={t('identity.first_name_placeholder')} autoComplete="given-name" className="w-full" startIcon={<User2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/>} disabled={isPending}/>
                                    <TextField control={form.control} name="lastName" label={t('last_name', { ns: 'common' })} placeholder={t('identity.last_name_placeholder')} autoComplete="family-name" className="w-full" startIcon={<User2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/>} disabled={isPending}/>
                                </form>
                            </Form>
                        </div>

                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setOpen(false)} disabled={isPending}>
                                {t('cancel', { ns: 'common' })}
                            </Button>

                            <Button type="button" onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
                                {isPending ? t('identity.saving') : t('identity.save')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>);
};
IdentityCard.displayName = 'IdentityCard';
